import { useState, useEffect, useCallback } from 'react';
import { useProcessedUsers } from '../../../hooks/useProcessedUsers';
import { useFilteredSortedData } from '../../../hooks/useFilteredSortedData';
import { UI_CONSTANTS } from '../../../constants/ui';
import { OrganizationData } from '../types';
import { USER_FILTER_KEYS } from '../constants';

export const usePaginatedUsers = (data: OrganizationData | null) => {
  const [filter, setFilter] = useState('');
  const [excludeFilter, setExcludeFilter] = useState(false);
  const [sort, setSort] = useState<{ key: string | null; dir: 'asc' | 'desc' }>({
    key: null,
    dir: 'asc'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(UI_CONSTANTS.PAGINATION_PAGE_SIZE);

  // Process user data using custom hook
  const processedUsers = useProcessedUsers({
    users: data?.users || [],
    projectDetails: data?.projectDetails || []
  });

  // Filtering and sorting using custom hook
  const filteredSortedUsers = useFilteredSortedData({
    data: processedUsers,
    filter,
    excludeFilter,
    sort,
    filterKeys: USER_FILTER_KEYS
  });

  // Paginate users to prevent performance issues
  const totalPages = Math.ceil(filteredSortedUsers.length / pageSize);
  const paginatedUsers = filteredSortedUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, excludeFilter, sort]);

  // Sort handler
  const handleSort = useCallback((key: string) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  return {
    filter,
    setFilter,
    excludeFilter,
    setExcludeFilter,
    sort,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    totalPages,
    paginatedUsers,
    filteredSortedUsers,
    processedUsers
  };
};
